import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import NavigationBar from './NavigationBar';
import { BarChart3, FlaskConical, TrendingDown, Trophy } from 'lucide-react';

const ADMIN_EMAIL = 'asher13a@gmail.com';

// Fetch wrapper for Meta API calls
async function metaApi(endpoint, token) {
  const res = await fetch(`https://graph.facebook.com/v21.0${endpoint}&access_token=${token}`);
  return res.json();
}

function AdsTracker() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [insights, setInsights] = useState({});
  const [adInsights, setAdInsights] = useState({});
  const [error, setError] = useState(null);
  const [metaToken, setMetaToken] = useState('');
  const [tokenInput, setTokenInput] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u && u.email === ADMIN_EMAIL) {
        // Try to load token from Firestore first, then localStorage
        try {
          const snap = await getDoc(doc(db, 'admin_config', 'meta_ads'));
          if (snap.exists() && snap.data().accessToken) {
            setMetaToken(snap.data().accessToken);
          } else {
            const local = localStorage.getItem('meta_ads_token');
            if (local) setMetaToken(local);
          }
        } catch {
          const local = localStorage.getItem('meta_ads_token');
          if (local) setMetaToken(local);
        }
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const fetchData = useCallback(async () => {
    if (!metaToken) return;

    try {
      setLoading(true);
      // Get all campaigns
      const campaignsRes = await metaApi(
        `/act_676655609053543/campaigns?fields=id,name,status,objective,daily_budget,created_time,start_time,stop_time,spend_cap&limit=50`,
        metaToken
      );

      if (campaignsRes.error) {
        setError(campaignsRes.error.message);
        setLoading(false);
        return;
      }

      // Only show Calypso campaigns, not old personal ads
      const allCampaigns = (campaignsRes.data || []).filter(c =>
        c.name && (c.name.toLowerCase().includes('calypso') || c.name.startsWith('Calypso'))
      );
      setCampaigns(allCampaigns);

      // Get insights for each campaign
      const insightsMap = {};
      const adInsightsMap = {};

      for (const c of allCampaigns) {
        try {
          const ins = await metaApi(
            `/${c.id}/insights?fields=impressions,clicks,ctr,cpc,cpm,spend,reach,frequency,actions,cost_per_action_type&date_preset=maximum`,
            metaToken
          );
          if (ins.data?.[0]) insightsMap[c.id] = ins.data[0];

          // Get ad-level insights
          const adsRes = await metaApi(
            `/${c.id}/ads?fields=id,name,status,effective_status,creative{id,name,body,title,image_url}&limit=50`,
            metaToken
          );
          const ads = adsRes.data || [];

          for (const ad of ads) {
            try {
              const adIns = await metaApi(
                `/${ad.id}/insights?fields=impressions,clicks,ctr,cpc,cpm,spend,actions&date_preset=maximum`,
                metaToken
              );
              if (adIns.data?.[0]) {
                adInsightsMap[ad.id] = { ...adIns.data[0], ad };
              } else {
                adInsightsMap[ad.id] = { ad, impressions: '0', clicks: '0', ctr: '0', cpc: '0', spend: '0' };
              }
            } catch (e) { /* skip */ }
          }
        } catch (e) { /* skip */ }
      }

      setInsights(insightsMap);
      setAdInsights(adInsightsMap);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [metaToken]);

  useEffect(() => {
    if (metaToken) fetchData();
  }, [metaToken, fetchData]);

  const saveToken = async () => {
    localStorage.setItem('meta_ads_token', tokenInput);
    setMetaToken(tokenInput);
    // Also save to Firestore so it persists across devices
    try {
      await setDoc(doc(db, 'admin_config', 'meta_ads'), { accessToken: tokenInput, updatedAt: new Date().toISOString() });
    } catch (e) { /* ok — falls back to localStorage */ }
  };

  // Auth gate
  if (loading) return <div className="app-route-canvas flex min-h-screen items-center justify-center"><div className="app-route-muted text-sm" role="status">Loading ads data…</div></div>;
  if (!user || user.email !== ADMIN_EMAIL) {
    return (
      <div className="app-route-canvas flex min-h-screen items-center justify-center p-6">
        <div className="app-route-surface app-route-border w-full max-w-md rounded-lg border p-8 text-center">
          <h1 className="text-lg font-semibold">Admin access required</h1>
          <p className="app-route-muted mt-2 text-sm">This route is only available to Calypso administrators.</p>
        </div>
      </div>
    );
  }

  // Token input
  if (!metaToken) {
    return (
      <div className="app-route-canvas min-h-screen pt-16">
        <NavigationBar />
        <main className="app-route-surface app-route-border mx-auto mt-12 max-w-md rounded-lg border p-6">
          <h1 className="mb-2 text-xl font-semibold">Connect Meta Ads</h1>
          <p className="app-route-muted mb-5 text-sm leading-6">Paste your Meta Marketing API access token to view ad performance.</p>
          <input
            type="password"
            data-private
            className="app-route-surface-muted app-route-border mb-3 w-full rounded-md border px-4 py-2.5 text-sm outline-none placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 dark:placeholder:text-white/40"
            placeholder="EAAc6eTjn..."
            value={tokenInput}
            onChange={e => setTokenInput(e.target.value)}
          />
          <button
            onClick={saveToken}
            className="min-h-11 w-full rounded-md bg-blue-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            Connect
          </button>
        </main>
      </div>
    );
  }

  // Aggregate stats
  const totalSpend = Object.values(insights).reduce((s, i) => s + parseFloat(i.spend || 0), 0);
  const totalClicks = Object.values(insights).reduce((s, i) => s + parseInt(i.clicks || 0), 0);
  const totalImpressions = Object.values(insights).reduce((s, i) => s + parseInt(i.impressions || 0), 0);
  const avgCtr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100) : 0;
  const avgCpc = totalClicks > 0 ? (totalSpend / totalClicks) : 0;
  const avgCpm = totalImpressions > 0 ? ((totalSpend / totalImpressions) * 1000) : 0;

  // Sort ads by CTR
  const sortedAds = Object.values(adInsights).sort((a, b) => parseFloat(b.ctr || 0) - parseFloat(a.ctr || 0));

  // Get learnings
  const bestAd = sortedAds[0];
  const worstAd = sortedAds[sortedAds.length - 1];

  return (
    <div className="app-route-canvas min-h-screen pb-20 pt-16">
      <NavigationBar />

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        {/* Header */}
        <header className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Ad Experiments</h1>
            <p className="app-route-muted mt-1 text-sm">Meta Ads performance tracker &amp; optimization dashboard</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchData}
              className="app-route-surface-muted app-route-border min-h-10 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:brightness-95 dark:hover:brightness-110"
            >
              Refresh
            </button>
            <button
              onClick={() => { localStorage.removeItem('meta_ads_token'); setMetaToken(''); }}
              className="app-route-surface-muted app-route-border min-h-10 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:brightness-95 dark:hover:brightness-110"
            >
              Disconnect
            </button>
          </div>
        </header>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <KPICard label="Total Spend" value={`$${totalSpend.toFixed(2)}`} />
          <KPICard label="Impressions" value={totalImpressions.toLocaleString()} />
          <KPICard label="Clicks" value={totalClicks.toLocaleString()} />
          <KPICard label="Avg CTR" value={`${avgCtr.toFixed(2)}%`} accent={avgCtr > 1 ? 'green' : avgCtr > 0.5 ? 'yellow' : 'red'} />
          <KPICard label="Avg CPC" value={`$${avgCpc.toFixed(2)}`} />
          <KPICard label="CPM" value={`$${avgCpm.toFixed(2)}`} />
        </div>

        {/* Tabs */}
        <div className="app-route-surface-muted app-route-border mb-6 flex w-full gap-1 overflow-x-auto rounded-md border p-1 sm:w-fit" role="tablist" aria-label="Ad experiment views">
          {['overview', 'ads', 'audiences', 'learnings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              role="tab"
              aria-selected={activeTab === tab}
              className={`min-h-10 rounded px-4 py-1.5 text-sm font-medium transition-colors ${
                activeTab === tab ? 'bg-blue-600 text-white' : 'app-route-muted hover:bg-[var(--app-surface)] hover:text-[var(--app-text)]'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {campaigns.length === 0 ? (
              <div className="app-route-muted py-16 text-center">
                <BarChart3 className="app-route-subtle mx-auto mb-3 h-8 w-8" aria-hidden="true" />
                <div className="text-lg font-medium">No campaigns yet</div>
                <div className="mt-1 text-sm">Run <code className="app-route-surface-muted rounded px-2 py-0.5">node ads/autopilot.js init</code> to create your first campaign</div>
              </div>
            ) : (
              campaigns.map(c => {
                const ins = insights[c.id];
                return (
                  <div key={c.id} className="app-route-surface app-route-border rounded-lg border p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="font-semibold">{c.name}</div>
                        <div className="app-route-subtle mt-0.5 text-xs">ID: {c.id} · Created: {new Date(c.created_time).toLocaleDateString()}</div>
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                    {ins ? (
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-3">
                        <MiniStat label="Impressions" value={parseInt(ins.impressions || 0).toLocaleString()} />
                        <MiniStat label="Clicks" value={ins.clicks || '0'} />
                        <MiniStat label="CTR" value={`${parseFloat(ins.ctr || 0).toFixed(2)}%`} />
                        <MiniStat label="CPC" value={`$${parseFloat(ins.cpc || 0).toFixed(2)}`} />
                        <MiniStat label="CPM" value={`$${parseFloat(ins.cpm || 0).toFixed(2)}`} />
                        <MiniStat label="Spend" value={`$${parseFloat(ins.spend || 0).toFixed(2)}`} />
                      </div>
                    ) : (
                      <div className="app-route-subtle text-sm">No data yet — campaign may be paused or just started</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'ads' && (
          <div className="space-y-2">
            {sortedAds.map((item, i) => {
              const ctr = parseFloat(item.ctr || 0);
              const isTop = i === 0 && ctr > 0;
              const isBottom = i === sortedAds.length - 1 && sortedAds.length > 2;
              const imageUrl = item.ad?.creative?.image_url;
              return (
                <div
                  key={item.ad?.id || i}
                  className={`rounded-xl p-4 mb-3 ${
                    isTop ? 'bg-emerald-500/10 border border-emerald-500/20' :
                    isBottom ? 'bg-red-500/5 border border-red-500/10' :
                    'app-route-surface app-route-border border'
                  }`}
                >
                  <div className="flex gap-4">
                    {/* Image preview */}
                    {imageUrl && (
                      <div className="flex-shrink-0">
                        <img
                          src={imageUrl}
                          alt="Ad creative"
                          className="app-route-border h-20 w-20 rounded-md border object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="app-route-subtle font-mono text-xs">#{i + 1}</span>
                          <StatusBadge status={item.ad?.effective_status || item.ad?.status} small />
                        </div>
                      </div>
                      <div className="truncate text-sm font-medium">{item.ad?.creative?.title || item.ad?.name || 'Unknown'}</div>
                      <div className="app-route-muted mt-0.5 line-clamp-2 text-xs">{item.ad?.creative?.body || ''}</div>
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
                        <MiniStat label="Impr." value={parseInt(item.impressions || 0).toLocaleString()} />
                        <MiniStat label="Clicks" value={item.clicks || '0'} />
                        <div>
                          <div className="text-sm font-semibold" style={{ color: ctr > 2 ? '#4ade80' : ctr > 1 ? '#a3e635' : ctr > 0.5 ? '#fbbf24' : ctr > 0 ? '#f87171' : 'rgba(255,255,255,0.3)' }}>
                            {ctr.toFixed(2)}%
                          </div>
                          <div className="app-route-subtle text-xs">CTR</div>
                        </div>
                        <MiniStat label="CPC" value={`$${parseFloat(item.cpc || 0).toFixed(2)}`} />
                        <MiniStat label="CPM" value={`$${parseFloat(item.cpm || 0).toFixed(2)}`} />
                        <MiniStat label="Spend" value={`$${parseFloat(item.spend || 0).toFixed(2)}`} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {sortedAds.length === 0 && (
              <div className="app-route-muted py-12 text-center text-sm">No ads found</div>
            )}
          </div>
        )}

        {activeTab === 'audiences' && (
          <div className="space-y-4">
            <p className="app-route-muted mb-4 text-sm leading-6">
              Audience segments being tested. The autopilot rotates through these every 2 weeks, killing the worst performer and testing a new one.
            </p>
            {[
              { key: 'retail_traders', label: 'Retail Traders / Day Traders', ages: '21-40', interests: 'Day trading, Stock market, Trading strategy', status: 'active' },
              { key: 'young_professionals', label: 'Young Finance Professionals', ages: '22-35', interests: 'Investment banking, Financial analysis, Fintech', status: 'active' },
              { key: 'personal_finance', label: 'Personal Finance Enthusiasts', ages: '25-45', interests: 'Personal finance, Stock market, Investment management', status: 'queued' },
              { key: 'fintech_savvy', label: 'Fintech / Tech-Forward Investors', ages: '21-38', interests: 'Financial technology, Day trading, Financial analysis', status: 'queued' },
              { key: 'institutional', label: 'Institutional / Buy-Side', ages: '25-55', interests: 'Investment management, Investment banking, Bloomberg', status: 'queued' },
            ].map(aud => (
              <div key={aud.key} className={`app-route-surface rounded-lg border p-5 ${aud.status === 'active' ? 'border-blue-500/30' : 'app-route-border'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold">{aud.label}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    aud.status === 'active' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-400' : 'app-route-surface-muted app-route-subtle'
                  }`}>
                    {aud.status === 'active' ? 'Testing' : 'Queued'}
                  </span>
                </div>
                <div className="app-route-muted text-xs">Ages {aud.ages} · {aud.interests}</div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'learnings' && (
          <div className="space-y-4">
            {totalClicks === 0 ? (
              <div className="app-route-muted py-16 text-center">
                <FlaskConical className="app-route-subtle mx-auto mb-3 h-8 w-8" aria-hidden="true" />
                <div className="text-lg font-medium">No learnings yet</div>
                <div className="text-sm mt-1">Learnings will appear here after ads accumulate enough impressions and clicks</div>
              </div>
            ) : (
              <>
                {bestAd && parseFloat(bestAd.ctr || 0) > 0 && (
                  <LearningCard
                    type="winner"
                    title="Best Performing Ad"
                    adName={bestAd.ad?.name}
                    copy={bestAd.ad?.creative?.body || bestAd.ad?.creative?.title}
                    ctr={parseFloat(bestAd.ctr || 0)}
                    cpc={parseFloat(bestAd.cpc || 0)}
                    impressions={parseInt(bestAd.impressions || 0)}
                  />
                )}
                {worstAd && sortedAds.length > 2 && (
                  <LearningCard
                    type="loser"
                    title="Worst Performing Ad"
                    adName={worstAd.ad?.name}
                    copy={worstAd.ad?.creative?.body || worstAd.ad?.creative?.title}
                    ctr={parseFloat(worstAd.ctr || 0)}
                    cpc={parseFloat(worstAd.cpc || 0)}
                    impressions={parseInt(worstAd.impressions || 0)}
                  />
                )}
                <div className="app-route-surface app-route-border rounded-lg border p-5">
                  <h3 className="mb-3 font-semibold">Aggressive Learning Strategy</h3>
                  <div className="app-route-muted space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">1.</span>
                      <span>$7/day across 6 ad variations and 2 audience segments</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">2.</span>
                      <span>Every 24h: analyze performance, kill bottom 50%, replace with smarter copy</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">3.</span>
                      <span>AI feeds past learnings (winners + losers) into new copy generation</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">4.</span>
                      <span>Every 5 days: swap worst-performing audience for a new untested one</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">5.</span>
                      <span>Every 5 days: generate new image creatives with Gemini</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">6.</span>
                      <span>Daily performance report emailed to asher13a@gmail.com</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">7.</span>
                      <span>Learns from 100 impressions (not 200) — faster iteration cycles</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function KPICard({ label, value, accent }) {
  const colorMap = { green: '#4ade80', yellow: '#fbbf24', red: '#f87171' };
  return (
    <div className="app-route-surface app-route-border rounded-lg border p-4 text-center">
      <div className="text-2xl font-semibold tabular-nums" style={accent ? { color: colorMap[accent] } : {}}>{value}</div>
      <div className="app-route-subtle mt-1 text-xs">{label}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-sm font-medium tabular-nums">{value}</div>
      <div className="app-route-subtle text-xs">{label}</div>
    </div>
  );
}

function StatusBadge({ status, small }) {
  const colors = {
    ACTIVE: 'bg-emerald-500/20 text-emerald-400',
    PAUSED: 'bg-yellow-500/15 text-yellow-400',
    DELETED: 'bg-red-500/15 text-red-400',
    ARCHIVED: 'app-route-surface-muted app-route-subtle',
    WITH_ISSUES: 'bg-orange-500/15 text-orange-400',
    PENDING_REVIEW: 'bg-blue-500/15 text-blue-400',
    IN_PROCESS: 'bg-blue-500/15 text-blue-400',
    DISAPPROVED: 'bg-red-500/20 text-red-400',
  };
  const labels = {
    WITH_ISSUES: 'IN REVIEW',
    PENDING_REVIEW: 'IN REVIEW',
    IN_PROCESS: 'IN REVIEW',
  };
  return (
    <span className={`${small ? 'px-1.5 py-0.5' : 'px-2 py-1'} rounded-full text-xs font-medium ${colors[status] || 'app-route-surface-muted app-route-subtle'}`}>
      {labels[status] || status}
    </span>
  );
}

function LearningCard({ type, title, adName, copy, ctr, cpc, impressions }) {
  const isWinner = type === 'winner';
  return (
    <div className={`rounded-lg border p-5 ${isWinner ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/10'}`}>
      <div className="flex items-center gap-2 mb-2">
        {isWinner ? <Trophy className="h-4 w-4 text-emerald-500" aria-hidden="true" /> : <TrendingDown className="h-4 w-4 text-red-500" aria-hidden="true" />}
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="app-route-muted mb-2 text-sm">{adName}</div>
      {copy && <div className="app-route-subtle mb-3 text-xs italic">"{copy}"</div>}
      <div className="app-route-muted flex flex-wrap gap-4 text-sm">
        <span className={isWinner ? 'text-emerald-400' : 'text-red-400'}>CTR: {ctr.toFixed(2)}%</span>
        <span>CPC: ${cpc.toFixed(2)}</span>
        <span>{impressions.toLocaleString()} impressions</span>
      </div>
    </div>
  );
}

export default AdsTracker;
